import { useState, useRef, useEffect } from "react";
import { Bot, X, Send } from "lucide-react";
import { api, WHATSAPP_URL } from "../lib/api";

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    {
      role: "bot",
      text: "Namaste. I am the legal assistant for Advocate Shubham Makkar. Briefly describe your matter (bail, FIR, anticipatory bail…) and share your phone number for a call-back.",
    },
  ]);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-chatbot", handler);
    return () => window.removeEventListener("open-chatbot", handler);
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { message: text });
      setMessages((m) => [...m, { role: "bot", text: data.reply, cta: data.cta }]);
    } catch {
      setMessages((m) => [...m, { role: "bot", text: "Please try again, or tap WhatsApp for instant chat." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 left-6 z-[60] w-14 h-14 rounded-full bg-gradient-to-br from-[#1D4ED8] to-[#0A2540] text-white flex items-center justify-center shadow-xl hover:scale-110 transition-transform"
        aria-label="Open chatbot"
        data-testid="chatbot-toggle"
      >
        {open ? <X size={22} /> : <Bot size={22} />}
      </button>

      {open && (
        <div
          className="fixed bottom-24 left-6 z-[60] w-[360px] max-w-[calc(100vw-3rem)] h-[480px] bg-white border border-slate-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          data-testid="chatbot-panel"
        >
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3 bg-gradient-to-r from-[#EFF6FF] to-white">
            <div className="w-9 h-9 bg-gradient-to-br from-[#1D4ED8] to-[#0A2540] flex items-center justify-center rounded-lg">
              <Bot size={16} className="text-white" />
            </div>
            <div>
              <div className="font-serif text-[#0A2540] text-base leading-none font-semibold">Legal Assistant</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#1D4ED8] mt-1 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span> Online · Advocate Shubham Makkar
              </div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed rounded-2xl ${m.role === "user"
                      ? "bg-[#1D4ED8] text-white rounded-br-sm"
                      : "bg-white text-slate-700 border border-slate-200 rounded-bl-sm shadow-sm"
                    }`}
                  data-testid={`chat-msg-${i}`}
                >
                  {m.text}
                  {m.cta === "whatsapp" && (
                    <a
                      href={WHATSAPP_URL()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block mt-2 text-xs underline text-[#25D366]"
                    >
                      → Open WhatsApp
                    </a>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white px-4 py-2.5 text-sm text-slate-400 border border-slate-200 rounded-2xl rounded-bl-sm shadow-sm">
                  typing…
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-slate-100 flex gap-2 bg-white">
            <input
              className="flex-1 bg-slate-50 border border-slate-200 px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-[#1D4ED8] focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/20 rounded-full transition-all"
              placeholder="Type your question…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              data-testid="chatbot-input"
            />
            <button
              onClick={send}
              disabled={loading}
              className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-50 transition-colors"
              data-testid="chatbot-send"
            >
              <Send size={15} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
